import React, { useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { wrapFunctionalComponent } from 'utils/framework';
import { screenReaderOnlyStyle } from 'style/config';
import Actions from 'Actions';
import Button from 'components/Button';
import localeUtils from 'utils/LanguageLocale';
import basketUtils from 'utils/Basket';
import addToCart from 'services/api/basket/addToCart';
import classifyAtbResult, { ATB_RESULT } from 'components/ProductPage/PdpBundling/BundleWidget/utils/classifyAtbResult';
import Empty from 'constants/empty';
import BundleWidgetEN from 'components/ProductPage/PdpBundling/BundleWidget/locales/BundleWidget_en_US';
import BundleWidgetFR from 'components/ProductPage/PdpBundling/BundleWidget/locales/BundleWidget_fr_CA';

const getText = localeUtils.getLocalisedDataFromResource(BundleWidgetEN, BundleWidgetFR);

const AddAllButton = ({ disabled, selectedItems = Empty.Array }) => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const dispatch = useDispatch();

    const itemCount = selectedItems.length;

    const showAtbErrorModal = useCallback(() => {
        dispatch(
            Actions.showInfoModal({
                isOpen: true,
                title: getText('atbErrorTitle'),
                message: getText('atbErrorMessage'),
                buttonText: getText('atbErrorOk')
            })
        );
    }, [dispatch]);

    const handleClick = useCallback(async () => {
        if (isSubmitting || !selectedItems.length) {
            return;
        }

        setIsSubmitting(true);

        const requestedSkuIds = selectedItems.map(sku => sku.skuId);
        const skuList = selectedItems.map(sku => ({
            skuId: sku.skuId,
            productId: sku.productId || '',
            qty: sku.qty || 1
        }));

        try {
            const response = await addToCart({ orderId: basketUtils.getOrderId(), skuList });
            const outcome = classifyAtbResult(requestedSkuIds, response);

            if (outcome === ATB_RESULT.ERROR) {
                showAtbErrorModal();
            } else {
                // TODO FER-797 (full) / FER-755 (partial): open the ATB Flyout modal.
                // When outcome === ATB_RESULT.PARTIAL, also show its inline error banner.
            }
        } catch {
            showAtbErrorModal();
        } finally {
            setIsSubmitting(false);
        }
    }, [isSubmitting, selectedItems, showAtbErrorModal]);

    const isDisabled = disabled || isSubmitting;

    return (
        <React.Fragment>
            <Button
                variant='primary'
                onClick={handleClick}
                disabled={isDisabled}
                aria-disabled={isDisabled}
                aria-busy={isSubmitting}
                aria-label={getText('addAllToBasketAria', [itemCount])}
                loading={isSubmitting}
            >
                {getText('addAllToBasket')}
            </Button>
            <span
                css={screenReaderOnlyStyle}
                role='status'
                aria-live='polite'
            >
                {isSubmitting ? getText('addAllToBasketLoading') : ''}
            </span>
        </React.Fragment>
    );
};

export default wrapFunctionalComponent(AddAllButton, 'AddAllButton');
